import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { EMPTY, Observable, defer, throwError, timer } from 'rxjs';
import { expand, first, switchMap, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AppState } from '../app.service';

/**
 * Practice Bot API — AI voice practice for micro-actions.
 *
 * Flow (see "Practice Bot — API Documentation"):
 *   0 · Action Hub card state  → getActionDetails + getPracticeSummary
 *   1 · Create session         → createPracticeSession
 *   2 · Voice call (WebRTC)    → connectRealtimeCall  (direct to OpenAI, NOT Foozi)
 *   3 · Upload + end           → uploadLearnerAudio / uploadPlaybackAudio / endPracticeSession
 *   4 · Poll analysis          → getPracticeSession / pollPracticeAnalysis
 *   5 · Award action points    → awardPracticeActionPoints  (Rails, NOT Foozi)
 *   6 · Show report            → render the analysis returned by step 4
 *   7 · Replay / history       → getPlaybackAudioUrl / getPracticeHistory / delete*
 *
 * Auth: every Foozi call needs the RAW 1st90 token (no `Bearer` prefix). AuthInterceptor already
 * force-sets that header on every HttpClient request; we set it explicitly too so each call is
 * self-describing and unit-testable.
 */

// ── Types ───────────────────────────────────────────────────────────────────────────────────────

export type PracticeSessionStatus =
  | 'active'
  | 'ended_awaiting_audio'
  | 'analyzing'
  | 'complete'
  | 'failed';

export type PracticeEndedReason = 'user_ended' | 'bot_ended' | 'timeout' | 'disconnected';

export type PracticeVerdict = 'met' | 'partial' | 'missed';

/** Card state for an Action Hub micro-action, derived from the Rails action + Foozi summary. */
export type PracticeCardState =
  | 'not_eligible'
  | 'skipped'
  | 'analyzing'
  | 'completed'
  | 'incomplete';

export interface PracticeScenario {
  scenario: string;
  persona_name: string;
  persona_role: string;
  learner_goal: string;
  /**
   * What the session actually runs with, after the server has resolved request → this action's
   * saved value → the learner's last choice → the path's language. Read these to show what is
   * active rather than what was asked for.
   */
  language?: string;
  accent?: string;
}

/**
 * One entry of `available_voices` on the defaults response.
 *
 * The shape changed: `gender`, `tone` and `description` are gone, replaced by `value` — the tone
 * id from the shared taxonomy, which is what the picker's rows are keyed by. The old fields are
 * kept optional so a server still answering the previous shape is read rather than ignored.
 */
export interface PracticeVoiceOption {
  id: string;
  label: string;
  /** Tone id, e.g. 'neutral-balanced'. Also the path segment for a demo clip. */
  value?: string;
  gender?: string;
  tone?: string;
}

/** The generated starting point for the preview + settings screens. No session exists yet. */
export interface PracticeDefaults {
  screen_content_id: number | string;
  persona: { name: string; description: string };
  scenario: string;
  rubric: { text: string };
  learner_goal: string;
  available_voices: PracticeVoiceOption[];
  degradations?: string[];
  /**
   * The sticky pair, for prefilling the settings screen: this action's saved value, else the
   * learner's last choice across actions, else empty.
   */
  language?: string;
  accent?: string;
}

/**
 * The editable settings, as `settings/refine` takes and returns them (flat keys, unlike the
 * nested defaults response).
 */
export interface PracticeSettings {
  persona_name?: string;
  persona_description?: string;
  scenario_text?: string;
  rubric_text?: string;
  learner_goal?: string;
  /** ISO 639-1 code (or a legacy full name). Empty means the path's own language. */
  language?: string;
  /** Free text, max 100 — "British", "South Indian". The server phrases it for the voice. */
  accent?: string;
}

/** One completed attempt, as both the in-step coach screen and the report feed list them. */
export interface PracticeCompletedAttempt {
  id: string;
  at: string;
  score: number | null;
  /** 1-based, oldest first. Label rows from this, never the array index, which runs the other way. */
  attemptNumber?: number;
  /**
   * How long the recording runs, in seconds — the server's `audio_duration_seconds` when it has
   * one, and its wall-clock `duration_seconds` when it does not (an attempt recorded before the
   * client started reporting the track length).
   */
  durationSeconds?: number;
  /** Whether there is a recording to play at all. */
  audioAvailable?: boolean;
}

/** An attempt in whatever state it reached — `complete` has a report, `failed` does not. */
export interface PracticeAttempt extends PracticeCompletedAttempt {
  status: string;
}

/** One line of the scenario-refinement conversation on the preview screen. */
export interface PracticeRefinementTurn {
  role: 'user' | 'coach';
  text: string;
  /**
   * When it was said, so practice attempts can be interleaved with the conversation instead of
   * being listed after it. Absent on turns stored before this was recorded.
   */
  at?: string;
}

/** What the learner shaped for one micro-action: the conversation and the settings it produced. */
export interface PracticeRefinementState {
  settings: PracticeSettings;
  history: PracticeRefinementTurn[];
  customContext: string;
  /**
   * Identifies the action wording these were shaped against. A micro-action keeps its
   * screen_content_id when it is customised, so without this a rewritten action would restore
   * the previous one's scenario.
   */
  actionFingerprint?: string;
}

/** The part of that worth writing to storage, so a reload does not lose it. */
export interface PracticeStoredConversation {
  history: PracticeRefinementTurn[];
  customContext: string;
  actionFingerprint?: string;
  /**
   * The settings the learner saved, voice pick included. `defaults` answers with what the admin
   * authored, so without these the learner's own version is replaced by the admin's on every
   * reload. "Reset to default" clears the record, which is how newer admin content gets through.
   */
  settings?: PracticeSettings & { voice_gender?: string; voice_tone?: string };
}

/** GET /voice-demos/{tone}/{lang} — a presigned clip URL and how long it stays good. */
export interface PracticeVoiceDemo {
  url: string;
  expires_in?: number;
}

export interface PracticeSessionCreateResponse {
  practice_session_id: string;
  client_secret: string;
  realtime_model: string;
  voice: string;
  max_duration_seconds: number;
  scenario: PracticeScenario;
  degradations: string[];
}

export interface PracticeTranscriptTurn {
  role: 'user' | 'assistant';
  text: string;
  /** Epoch ms from the client clock — required, they drive the response-latency metric. */
  started_at_ms: number;
  ended_at_ms: number;
}

export interface PracticeSummaryAction {
  screen_content_id: string;
  attempt_count: number;
  completed_count: number;
  last_status: PracticeSessionStatus;
  last_performance_score: number | null;
  best_performance_score: number | null;
  last_practice_session_id: string;
  last_attempted_at: string;
}

export interface PracticeCriterionResult {
  id: string;
  label: string;
  weight: number;
  score: number;
  verdict: PracticeVerdict;
  evidence: string[];
  suggestion: string;
}

/**
 * The score a learner is working towards, shown as the target mark on the report's meter.
 *
 * Hardcoded because it has to be: the API documents ready_threshold as a field that does not exist
 * yet. The History list does NOT derive its ready chip from this — /practice returns its own
 * recommendation, and the server's verdict wins over an assumption made here.
 */
export const PRACTICE_SCORE_TARGET = 70;

/** One micro-action in the practice history, as /practice groups it. */
export interface PracticeHistoryGroup {
  screenContentId: number | string;
  title: string;
  /**
   * The action's wording, as the practice bot's scenario card shows it.
   *
   * A row opens the bot for the action, and the bot is handed the action — title and wording both.
   * Without this the card opened with a heading and nothing under it.
   */
  description: string;
  /** Every attempt, including ones still active or analyzing. */
  sessionCount: number;
  /** Just the ones that produced a report. */
  completedCount: number;
  /** Completed scores, oldest first — the "41 → 55 → 64" progression on the card. */
  scores: number[];
  /** 'ready' · 'practice_again', from the most recent completed attempt; null until one completes. */
  recommendation: string | null;
  /** recommendation === 'ready', named for what the card asks. */
  isReady: boolean;
  latestAt: string;
  /** The attempt whose report a row opens. */
  latestSessionId: string;
  sessionIds: string[];
  /** One page of this action's attempts, newest first. */
  sessions: any[];
  /** Whether older attempts remain for THIS action — fetch them by advancing its own offset. */
  hasMoreSessions: boolean;
  /**
   * The cycle this action belongs to. History spans every cycle the learner has been through, and
   * an action from an earlier one behaves differently, so the row has to be able to say which.
   */
  cycleId: number | string | null;
  cycleName: string;
}

export interface PracticeAnalysis {
  status: PracticeSessionStatus;
  /** Headline score 0–100. Build against this — `confidence_score` is a deprecated alias. */
  performance_score: number;
  score_breakdown?: {
    content: number;
    delivery: number;
    content_weight: number;
    delivery_weight: number;
  };
  content: PracticeCriterionResult[];
  delivery: {
    pace_wpm: number;
    pauses: { count: number; total_seconds: number };
    avg_response_latency_ms: number;
    fillers: { count: number; examples: string[] };
    hedges: { count: number; examples: string[] };
    observations: string[];
  };
  summary: { strengths: string[]; improvements: string[]; next_focus: string };
  trend: Array<{ practice_session_id: string; performance_score: number; created_at: string }>;
  rubric: { source: 'authored' | 'derived' | 'default'; criteria: any[] };
  language: string;
  degradations: string[];
  /** The recording's true length in seconds. Null until the client reported one at /end. */
  audio_duration_seconds?: number | null;
  /** Whether a recording exists to play. Absent on a server that predates the field. */
  audio_available?: boolean;
}

/** What `award_practice_action_points` credited. `points_awarded: 0` means nothing was owed. */
export interface PracticeAwardResponse {
  status: boolean;
  message: string;
  data: {
    awarded: Array<{
      screen_content_id: number;
      points: number;
      sessions_credited: number;
      completed_sessions: number;
    }>;
    points_awarded: number;
    /** The learner's new total for the active path. Null when nothing was credited. */
    earned_action_points: number | null;
    /** True when the named session exists but has not reached `complete` yet — retry. */
    pending_session: boolean;
  };
}

// ── Service ─────────────────────────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class PracticeBotService {
  /** https://api-stage.1st90.com/chat/api  +  /v2/practice */
  readonly PRACTICE_URL = `${environment.fooziBaseUrl}/v2/practice`;
  /** Rails API — the committed-actions list that carries `practiceEligible`. */
  readonly APP_API_URL = environment.apiBaseUrl;
  readonly GET_ACTION_DETAILS = '/get_action_details';
  /** Rails — the only endpoint that credits action points for practice (APP-3275). */
  readonly AWARD_PRACTICE_POINTS = '/award_practice_action_points';

  /** OpenAI realtime handshake (step 2) — a direct third-party call, not Foozi. */
  readonly OPENAI_REALTIME_URL = 'https://api.openai.com/v1/realtime/calls';
  readonly DEFAULT_REALTIME_MODEL = 'gpt-realtime';

  /** Server rejects anything larger; fail fast instead of uploading 25 MB+ and 4xx-ing. */
  readonly MAX_AUDIO_BYTES = 25 * 1024 * 1024;

  constructor(private http: HttpClient, private appState: AppState) {}

  /** Raw 1st90 token — no `Bearer` prefix (Foozi rejects it). */
  private authHeaders(): HttpHeaders {
    return new HttpHeaders().set('Authorization', localStorage.getItem('token') || '');
  }

  // ── Step 0 · Action Hub card state ───────────────────────────────────────────

  /** Rails: committed actions, each with `screenContentId` + `practiceEligible`. */
  getActionDetails(): Observable<any> {
    return this.http.get(this.APP_API_URL + this.GET_ACTION_DETAILS, { headers: this.authHeaders() });
  }

  /**
   * Foozi: aggregated practice state per `screen_content_id`. Only actions with at least one
   * session appear — absent means never tried (render as "skipped").
   */
  getPracticeSummary(): Observable<{ actions: PracticeSummaryAction[] }> {
    return this.http.get<{ actions: PracticeSummaryAction[] }>(`${this.PRACTICE_URL}/summary`, {
      headers: this.authHeaders(),
    });
  }

  /** Index a summary response by `screen_content_id` for O(1) lookup per action card. */
  indexSummaryByAction(summary: { actions?: PracticeSummaryAction[] }): Record<string, PracticeSummaryAction> {
    const actions = summary?.actions ?? [];
    return actions.reduce((acc, entry) => {
      acc[String(entry?.screen_content_id)] = entry;
      return acc;
    }, {} as Record<string, PracticeSummaryAction>);
  }

  /**
   * Card state for one action. `state` is that action's entry from the summary (undefined when the
   * action has never been practised).
   */
  derivePracticeCardState(practiceEligible: boolean, state?: PracticeSummaryAction): PracticeCardState {
    if (!practiceEligible) { return 'not_eligible'; }   // no practice CTA at all
    if (!state) { return 'skipped'; }                    // eligible but never tried
    if (state.last_status === 'analyzing') { return 'analyzing'; }
    if ((state.completed_count ?? 0) > 0) { return 'completed'; }
    return 'incomplete';                                 // attempted, never finished
  }

  /**
   * Score for the persistent chip on an action card: the best ever, not the latest.
   * (`last_performance_score` belongs on the session report screen only.)
   */
  getCardScore(state?: PracticeSummaryAction): number | null {
    return state?.best_performance_score ?? null;
  }

  // ── Practice settings · defaults + refine (no session created) ───────────────

  /**
   * The refined settings themselves are kept by the backend — `defaults` hands them back on a
   * later load. The refinement *conversation* is not: no endpoint returns the learner's messages.
   * So it is held in two tiers, both keyed by micro-action:
   *
   *  - this Map, for as long as the app runs (this service is a root singleton, so it outlives
   *    the modal). Carries the settings too, so a re-open cannot be undercut by `defaults`.
   *  - localStorage, so the conversation also survives a page reload. Only the messages go there;
   *    the settings come from `defaults`, which is the newer source after a reload.
   *
   * Scoped per user, because logging out clears only `token` and `user` from localStorage.
   */
  private refinementStates = new Map<string, PracticeRefinementState>();
  readonly CONVERSATION_STORAGE_PREFIX = 'practiceRefinement';
  /** Cap on stored messages, so a long conversation cannot grow the entry without bound. */
  readonly MAX_STORED_TURNS = 50;

  /** Same-app-run state, settings included. Undefined after a reload — see getStoredConversation. */
  getRefinementState(screenContentId: number | string): PracticeRefinementState | undefined {
    return this.refinementStates.get(String(screenContentId));
  }

  saveRefinementState(screenContentId: number | string, state: PracticeRefinementState): void {
    this.refinementStates.set(String(screenContentId), state);
    this.writeStoredConversation(screenContentId, {
      history: (state?.history ?? []).slice(-this.MAX_STORED_TURNS),
      customContext: state?.customContext ?? '',
      actionFingerprint: state?.actionFingerprint,
      settings: state?.settings ? { ...state.settings } : undefined,
    });
  }

  /** "Reset to default" — drop what the learner shaped so freshly generated defaults apply. */
  clearRefinementState(screenContentId: number | string): void {
    this.refinementStates.delete(String(screenContentId));
    try {
      localStorage.removeItem(this.conversationStorageKey(screenContentId));
    } catch {
      // Storage unavailable (private mode, quota) — the in-memory tier is still correct.
    }
  }

  /** The conversation as last written to storage. Survives a reload; carries no settings. */
  getStoredConversation(screenContentId: number | string): PracticeStoredConversation | undefined {
    try {
      const raw = localStorage.getItem(this.conversationStorageKey(screenContentId));
      if (!raw) { return undefined; }
      const parsed = JSON.parse(raw);
      const history = Array.isArray(parsed?.history) ? parsed.history : [];
      // Saved settings matter on their own — a learner may have saved without ever chatting.
      if (!history.length && !parsed?.settings) { return undefined; }
      return {
        history,
        customContext: parsed?.customContext ?? '',
        actionFingerprint: parsed?.actionFingerprint,
        settings: parsed?.settings,
      };
    } catch {
      // Unreadable or hand-edited entry — treat it as absent rather than breaking the modal.
      return undefined;
    }
  }

  private writeStoredConversation(
    screenContentId: number | string,
    conversation: PracticeStoredConversation
  ): void {
    try {
      localStorage.setItem(
        this.conversationStorageKey(screenContentId),
        JSON.stringify(conversation)
      );
    } catch {
      // Storage unavailable — the conversation still survives modal re-opens in memory.
    }
  }

  private conversationStorageKey(screenContentId: number | string): string {
    const userId = this.appState?.get('currentUser')?.id ?? 'anon';
    return `${this.CONVERSATION_STORAGE_PREFIX}:${userId}:${screenContentId}`;
  }

  readonly SUPERSEDED_STORAGE_PREFIX = 'practiceSuperseded';
  /** Cap, so a much-reworded action cannot grow its entry without bound. */
  readonly MAX_STORED_SUPERSEDED = 100;

  /**
   * Attempts hidden because the action was reworded after they were practised. Held in storage
   * rather than only in the screen that superseded them: otherwise leaving the micro-action and
   * coming back shows attempts practised against wording the learner can no longer see, and
   * "View Feedback" reports on a scenario that no longer matches the action.
   *
   * Per device, and therefore a mitigation, not the whole answer: the complete fix is a revision or
   * customization id stored on each practice session and filtered by the history endpoint, which
   * nothing on the client can substitute for across devices.
   */
  getSupersededAttemptIds(screenContentId: number | string): string[] {
    try {
      const raw = localStorage.getItem(this.supersededStorageKey(screenContentId));
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((id: any) => typeof id === 'string') : [];
    } catch {
      // Unreadable or hand-edited entry — treat it as absent rather than hiding nothing at all.
      return [];
    }
  }

  addSupersededAttemptIds(screenContentId: number | string, ids: string[]): void {
    if (!screenContentId || !ids?.length) { return; }
    const merged = Array.from(new Set([...this.getSupersededAttemptIds(screenContentId), ...ids]));
    try {
      localStorage.setItem(
        this.supersededStorageKey(screenContentId),
        JSON.stringify(merged.slice(-this.MAX_STORED_SUPERSEDED))
      );
    } catch {
      // Storage unavailable (private mode, quota) — the in-memory set still hides them for now.
    }
  }

  private supersededStorageKey(screenContentId: number | string): string {
    const userId = this.appState?.get('currentUser')?.id ?? 'anon';
    return `${this.SUPERSEDED_STORAGE_PREFIX}:${userId}:${screenContentId}`;
  }

  /**
   * The LLM-generated persona/scenario/rubric/goal plus the selectable voices, shown on the
   * preview screen and used to pre-fill the settings form.
   * Errors: 403 not eligible · 404 not found · 503 LLM unavailable (fallback defaults come back
   * with `degradations` populated).
   */
  getPracticeDefaults(screenContentId: number | string): Observable<PracticeDefaults> {
    const params = new HttpParams().set('screen_content_id', screenContentId);
    return this.http.get<PracticeDefaults>(`${this.PRACTICE_URL}/defaults`, {
      headers: this.authHeaders(),
      params,
    });
  }

  /**
   * A pre-generated clip of one tone spoken in one language, for the picker's preview.
   *
   * Every tone in the taxonomy has clips — including the three that cannot start a live session —
   * so this is not limited to what `available_voices` offers. The URL is presigned and good for
   * about five days, and the server hands the same one to every caller for that period, so the
   * browser can cache the audio itself.
   *
   * A tone/language pair that was never generated is not an error here: the endpoint signs the key
   * regardless and the 404 arrives from S3 when the clip is fetched.
   */
  getVoiceDemoUrl(toneValue: string, langCode: string): Observable<PracticeVoiceDemo> {
    const tone = encodeURIComponent((toneValue || '').trim());
    const language = encodeURIComponent((langCode || '').trim());
    return this.http.get<PracticeVoiceDemo>(
      `${this.PRACTICE_URL}/voice-demos/${tone}/${language}`,
      { headers: this.authHeaders() },
    );
  }

  /**
   * Forgets the learner's saved settings server-side, so `defaults` answers with the admin's
   * authored setup again. This is the server half of "Reset to default".
   */
  deletePracticeSettings(screenContentId: number | string): Observable<any> {
    const params = new HttpParams().set('screen_content_id', screenContentId);
    return this.http.delete(`${this.PRACTICE_URL}/settings`, {
      headers: this.authHeaders(),
      params,
    });
  }

  /**
   * Rewrites the settings from a natural-language instruction ("make Sam more skeptical") and
   * returns the updated set. Send the current settings as the body — manual edits are the
   * starting point for the refinement. `refinement_prompt` is required; the endpoint 422s
   * without it.
   */
  refinePracticeSettings(
    payload: PracticeSettings & { screen_content_id: number | string; refinement_prompt: string }
  ): Observable<PracticeSettings> {
    // Blank means reuse, not clear: sending "" would be read as a value and a persona-only edit
    // would wipe a saved accent. Omitted, the server keeps what it has.
    const body: any = { ...payload };
    ['language', 'accent'].forEach((field) => {
      if (!(body[field] || '').toString().trim()) { delete body[field]; }
    });
    return this.http.post<PracticeSettings>(`${this.PRACTICE_URL}/settings/refine`, body, {
      headers: this.authHeaders(),
    });
  }

  /**
   * The learner picks a gender and a tone; the session body wants one of the `voice_id`s that
   * `defaults` offered. Exact match first, then anything of the right gender, then whatever is on
   * offer — and undefined if `defaults` returned no voices, which leaves the backend on its own
   * default rather than sending it an id it never advertised.
   */
  resolveVoiceId(voices: PracticeVoiceOption[], gender?: string, tone?: string): string | undefined {
    const list = voices ?? [];
    if (!list.length) { return undefined; }
    const wantedTone = (tone || '').toLowerCase();
    // `value` is where the tone lives now; `tone` is the old field, still read so a server on the
    // previous shape keeps working. Gender is gone from the payload entirely — the argument is
    // kept so callers need not change, and only narrows a match when the field is still there.
    const wantedGender = (gender || '').toLowerCase();
    const toneOf = (v: PracticeVoiceOption) => (v?.value || v?.tone || '').toLowerCase();
    // The new shape has no gender at all, so the tone is the whole of the choice and is matched
    // across the list. A payload still carrying gender keeps the old precedence — exact pair
    // first, then gender alone — which is what its rows were built to answer.
    const isNewShape = list.some((v) => !!v?.value) || list.every((v) => !v?.gender);
    if (wantedTone && isNewShape) {
      const byTone = list.find((v) => toneOf(v) === wantedTone);
      if (byTone) { return byTone.id; }
    }
    const matches = (v: PracticeVoiceOption, g: boolean, t: boolean) =>
      (!g || (v?.gender || '').toLowerCase() === wantedGender) &&
      (!t || toneOf(v) === wantedTone);
    return (
      list.find((v) => matches(v, !!wantedGender, !!wantedTone)) ??
      list.find((v) => matches(v, !!wantedGender, false)) ??
      list[0]
    )?.id;
  }

  // ── Step 1 · Create a practice session ───────────────────────────────────────

  /**
   * Mints the session + a short-lived OpenAI `client_secret` and returns the scenario to show
   * before the call. Errors: 403 not eligible · 404 not found · 429 rate limit (10/hour, honour
   * `Retry-After`) · 503 voice unavailable.
   */
  createPracticeSession(payload: {
    screenContentId: number | string;
    mode?: string;
    customContext?: string;
    voiceId?: string;
    /**
     * The settings as the learner last saw them. Sending all four of persona_name,
     * persona_description, scenario_text and rubric_text takes the backend's OVERRIDE path: it
     * templates the instructions directly instead of generating them (~1s of the wait before the
     * coach's first word), and — more importantly — guarantees the call runs the scenario the
     * preview screen showed, refinements included. Any field left blank is omitted, which drops
     * the whole request back to full LLM generation.
     */
    settings?: PracticeSettings;
    /** Overrides for this session. Omitted when blank, which reuses the sticky values. */
    language?: string;
    accent?: string;
  }): Observable<PracticeSessionCreateResponse> {
    const body: any = {
      screen_content_id: payload?.screenContentId,
      mode: payload?.mode || 'roleplay',
    };
    // NOT part of the documented session body (the API flow doc lists eight keys, this is not one
    // of them). Kept because the earlier API spec carried it, but only sent when it actually holds
    // something — an empty string is noise either way. Confirm with the backend before relying on it.
    const customContext = (payload?.customContext || '').trim();
    if (customContext) { body.custom_context = customContext; }
    const settings = payload?.settings;
    const overrides: Array<keyof PracticeSettings> =
      ['persona_name', 'persona_description', 'scenario_text', 'rubric_text', 'learner_goal'];
    overrides.forEach((field) => {
      const value = (settings?.[field] ?? '').trim();
      if (value) { body[field] = value; }
    });
    // The learner's voice pick. Left out when they have not chosen one, so the backend keeps
    // whichever voice it would have defaulted to.
    if (payload?.voiceId) { body.voice_id = payload.voiceId; }
    // Same rule as the settings save: a blank field reuses the saved value, so it is omitted
    // rather than sent empty. Creating a session also SAVES whatever is resolved, per-action and
    // per-user — so starting a call with an accent makes it stick without a separate save.
    const language = (payload?.language || '').trim();
    if (language) { body.language = language; }
    const accent = (payload?.accent || '').trim().slice(0, 100);
    if (accent) { body.accent = accent; }
    return this.http.post<PracticeSessionCreateResponse>(`${this.PRACTICE_URL}/session`, body, {
      headers: this.authHeaders(),
    });
  }

  // ── Step 2 · Voice call (direct to OpenAI) ───────────────────────────────────

  /**
   * Exchanges the local SDP offer for OpenAI's SDP answer.
   *
   * Deliberately uses `fetch`, not HttpClient: AuthInterceptor force-sets the 1st90 token on every
   * HttpClient request, which would overwrite the `Bearer <client_secret>` this endpoint requires.
   */
  readonly REALTIME_HANDSHAKE_TIMEOUT_MS = 15000;

  async connectRealtimeCall(clientSecret: string, sdpOffer: string, model?: string): Promise<string> {
    const url = `${this.OPENAI_REALTIME_URL}?model=${encodeURIComponent(model || this.DEFAULT_REALTIME_MODEL)}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.REALTIME_HANDSHAKE_TIMEOUT_MS);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${clientSecret}`,
          'Content-Type': 'application/sdp',
        },
        body: sdpOffer,
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error(`Realtime handshake failed: ${response.status} ${response.statusText}`);
      }
      return await response.text();   // SDP answer → pass to setRemoteDescription()
    } finally {
      clearTimeout(timer);
    }
  }

  // ── Step 3 · Upload audio + end the session ──────────────────────────────────

  /** Learner mic only — this is what drives the analysis. Raw body, NOT multipart. */
  uploadLearnerAudio(sessionId: string, audio: Blob): Observable<any> {
    return this.uploadAudio(sessionId, audio);
  }

  /** Mixed learner+bot recording, stored for replay. Does NOT trigger analysis. */
  uploadPlaybackAudio(sessionId: string, audio: Blob): Observable<any> {
    return this.uploadAudio(sessionId, audio, 'playback');
  }

  private uploadAudio(sessionId: string, audio: Blob, kind?: 'playback'): Observable<any> {
    // rxjs 6: throwError takes the error itself, not a factory.
    if (!audio) {
      return throwError(new Error('No audio to upload'));
    }
    if (audio.size > this.MAX_AUDIO_BYTES) {
      return throwError(new Error(`Audio exceeds the ${this.MAX_AUDIO_BYTES} byte limit`));
    }
    // Honour the recorder's own MIME: iOS records mp4 in this WebView, everywhere else webm. The
    // fallback is deliberately NOT audio/webm — labelling an iOS mp4 as webm is exactly what makes
    // the replay unplayable on iOS, which trusts the served Content-Type where Chrome sniffs the
    // bytes and plays it anyway. If the recorder gave us no type, say so rather than guess wrong.
    const headers = this.authHeaders().set('Content-Type', audio.type || 'application/octet-stream');
    const params = kind ? new HttpParams().set('kind', kind) : undefined;
    return this.http.post(`${this.PRACTICE_URL}/${sessionId}/audio`, audio, { headers, params });
  }

  /**
   * Submits the transcript and closes the session. Analysis kicks off once BOTH this and the
   * learner-audio upload have landed — whichever arrives second triggers it.
   *
   * The recorded audio is mic-only, so the bot's turn boundaries exist ONLY in this transcript;
   * `started_at_ms` / `ended_at_ms` are required for the latency metric.
   */
  endPracticeSession(
    sessionId: string,
    transcript: PracticeTranscriptTurn[],
    endedReason: PracticeEndedReason = 'user_ended',
    audioDurationSeconds?: number
  ): Observable<any> {
    const body: any = { transcript: transcript ?? [], ended_reason: endedReason };
    // Optional, and omitted rather than sent as null: /end is retried idempotently and the server
    // keeps the first value it was given, so a retry that has no length must not offer one. A
    // negative — or a NaN from an unmeasurable recording — is a 422, so neither is sent.
    if (typeof audioDurationSeconds === 'number'
      && isFinite(audioDurationSeconds) && audioDurationSeconds >= 0) {
      body.audio_duration_seconds = audioDurationSeconds;
    }
    return this.http.post(
      `${this.PRACTICE_URL}/${sessionId}/end`,
      body,
      { headers: this.authHeaders() }
    );
  }

  // ── Step 4 · Poll for the analysis result ────────────────────────────────────

  /** One status/analysis read. While `analyzing`, keep polling; on `complete` this IS the report. */
  getPracticeSession(sessionId: string): Observable<PracticeAnalysis> {
    return this.http.get<PracticeAnalysis>(`${this.PRACTICE_URL}/${sessionId}`, {
      headers: this.authHeaders(),
    });
  }

  /**
   * Polls until the session reaches a terminal status (`complete` or `failed`) and emits once.
   * Caller renders the report on `complete` and an error state on `failed`.
   *
   * Backs off 2s → 3s → 4.5s … capped at `maxIntervalMs`, instead of hammering a fixed 2.5s for the
   * whole window: analysis usually lands in the first few seconds, and a fixed interval costs ~48
   * requests over the 2-minute timeout versus ~20 here. The first read is immediate, so a session
   * that is already complete resolves in one call.
   */
  pollPracticeAnalysis(
    sessionId: string,
    options: { intervalMs?: number; maxIntervalMs?: number; timeoutMs?: number } = {}
  ): Observable<PracticeAnalysis> {
    const firstIntervalMs = options.intervalMs ?? 2000;   // doc: every ~2–3s
    const maxIntervalMs = options.maxIntervalMs ?? 8000;
    const timeoutMs = options.timeoutMs ?? 120000;
    const isTerminal = (result: PracticeAnalysis) =>
      result?.status === 'complete' || result?.status === 'failed';

    return defer(() => {
      let delayMs = 0;
      return this.getPracticeSession(sessionId).pipe(
        expand((result) => {
          if (isTerminal(result)) { return EMPTY; }      // stop — no further requests
          delayMs = delayMs ? Math.min(Math.round(delayMs * 1.5), maxIntervalMs) : firstIntervalMs;
          return timer(delayMs).pipe(switchMap(() => this.getPracticeSession(sessionId)));
        }),
        first(isTerminal)
      );
    }).pipe(timeout(timeoutMs));
  }

  // ── Step 5 · Award action points ─────────────────────────────────────────────

  /**
   * Credits action points for the learner's completed practice sessions: 10 per completed
   * session, no cap on repeats.
   *
   * The payload only tells the server WHERE to look — it reads Foozi's own session records to
   * decide what to award, so this can never over-claim. Safe to call more than once: a second
   * call for the same attempt credits nothing.
   */
  awardPracticeActionPoints(
    payload: { practiceSessionId?: string; screenContentId?: number | string } = {}
  ): Observable<PracticeAwardResponse> {
    const body: any = {};
    if (payload?.practiceSessionId) { body.practice_session_id = payload.practiceSessionId; }
    if (payload?.screenContentId != null) { body.screen_content_id = payload.screenContentId; }
    return this.http.post<PracticeAwardResponse>(
      this.APP_API_URL + this.AWARD_PRACTICE_POINTS,
      body,
      { headers: this.authHeaders() }
    );
  }

  // ── Step 7 · Replay & history ────────────────────────────────────────────────

  /** Presigned S3 URL for playback (prefers the mixed clip). */
  getPlaybackAudioUrl(sessionId: string): Observable<{ url: string; expires_in: number }> {
    return this.http.get<{ url: string; expires_in: number }>(
      `${this.PRACTICE_URL}/${sessionId}/audio`,
      { headers: this.authHeaders() }
    );
  }

  /**
   * The learner's practice history, grouped by micro-action and newest action first.
   *
   * Two independent pagings, which is why the options read oddly at first glance:
   *  - actionLimit/actionOffset walk the CARDS — the History tab's list of micro-actions;
   *  - limit/offset walk ONE card's attempts, and the server rejects an offset without a
   *    screenContentId, because skipping attempts only means something within a single action.
   *
   * Without a screenContentId every action returns its own first page of attempts, so the
   * most-practised one cannot crowd out the rest.
   */
  getPracticeHistory(
    screenContentId?: number | string,
    options: { limit?: number; offset?: number; actionLimit?: number; actionOffset?: number } = {},
  ): Observable<any> {
    let params = new HttpParams();
    if (screenContentId != null) {
      params = params.set('screen_content_id', String(screenContentId));
    }
    // Clamped to the documented ranges: outside them the call is a 422, which is a worse outcome
    // than a page one row off at the extreme.
    if (options.limit != null) {
      params = params.set('limit', String(Math.min(Math.max(options.limit, 1), 50)));
    }
    // An offset without an action is a 422 — drop it rather than send a request that cannot succeed.
    if (options.offset != null && screenContentId != null) {
      params = params.set('offset', String(Math.max(options.offset, 0)));
    }
    if (options.actionLimit != null) {
      params = params.set('action_limit', String(Math.min(Math.max(options.actionLimit, 1), 100)));
    }
    if (options.actionOffset != null) {
      params = params.set('action_offset', String(Math.max(options.actionOffset, 0)));
    }
    return this.http.get(this.PRACTICE_URL, { headers: this.authHeaders(), params });
  }

  /**
   * Every attempt in a history response, oldest first, whatever state it reached. Tolerates the
   * envelope shapes the endpoint may use, and skips sessions with no id since nothing can be
   * opened or tracked against them.
   */
  attemptsFrom(response: any): PracticeAttempt[] {
    return this.sessionListFrom(response)
      .map((session: any) => ({
        id: session?.practice_session_id ?? session?.id,
        at: session?.created_at ?? session?.ended_at,
        score: session?.performance_score ?? null,
        status: session?.status ?? '',
        attemptNumber: session?.attempt_number,
        // The true track length when the server has one, which is what the scrubber is measured
        // against. `duration_seconds` now prefers it too, but falls back to the wall clock — the
        // call's own length, minutes longer than the recording on a session that sat idle.
        //
        // A reported ZERO is not a length. The write accepts `>= 0`, so it is a value the store can
        // genuinely hold, and taken at face value it turned a row that read "03:12" into "0:00".
        // The report screen has always measured it this way; this is the same rule, one list along.
        durationSeconds: this.reportedAudioSeconds(session) ?? session?.duration_seconds,
        audioAvailable: session?.audio_available,
      }))
      .filter((attempt) => !!attempt.id)
      .sort((a, b) => new Date(a.at || 0).getTime() - new Date(b.at || 0).getTime());
  }

  /**
   * The recording's own length as the server reports it, or null where it has none to report.
   *
   * Null and zero are both "no length here": the field is null until a client reports one, and the
   * endpoint accepts `>= 0`, so a zero-length report is storable. Neither can measure a scrubber.
   */
  private reportedAudioSeconds(session: any): number | null {
    const reported = session?.audio_duration_seconds;
    return typeof reported === 'number' && reported > 0 ? reported : null;
  }

  /**
   * The cycle an action belongs to, however this endpoint spells it.
   *
   * /practice answers with cycleId and cycleName — camelCase, alone among that object's fields,
   * every one of its neighbours being snake_case (screen_content_id, session_count). /customizations
   * answers with cycle_id and cycle_name. Both are read here rather than one being called correct:
   * a row losing its label to a spelling is not worth a round trip to find out which end to change.
   */
  private cycleOf(action: any): { cycleId: number | string | null; cycleName: string } {
    const id = action?.cycleId ?? action?.cycle_id ?? null;
    const name = action?.cycleName ?? action?.cycle_name ?? '';
    return { cycleId: id ?? null, cycleName: String(name ?? '').trim() };
  }

  /**
   * The History tab's rows, one per micro-action, straight off the server.
   *
   * Grouping, titles and the ready/practice-again verdict used to be reconstructed here; /practice
   * returns them now, so this only reshapes the envelope into the names the UI reads.
   *
   * The totals do NOT page: session_count, completed_count and confidence_scores are aggregates
   * over every attempt, so a card showing 10 of 12 still reads "12 sessions" and its progression
   * still starts at the learner's first ever score.
   */
  practiceActionsFrom(response: any): PracticeHistoryGroup[] {
    const actions = Array.isArray(response?.actions) ? response.actions : [];
    // The History row names the cycle an action belongs to, and /practice is the only place it can
    // learn it from. Said out loud once per response rather than leaving a silent gap on the row,
    // and the two cases are worth telling apart: a field that never arrived is a different ask of
    // the backend than one that arrives empty.
    if (actions.length && !actions.some((action: any) => this.cycleOf(action).cycleName)) {
      console.log(
        '[practice] no cycle on these actions, under any spelling this reads; keys were:',
        Object.keys(actions[0] || {}),
      );
    }
    return actions
      .map((action: any) => {
        const sessions: any[] = action?.sessions || [];
        // sessions[] is newest first. Only `complete` attempts have a report, so the row opens the
        // newest COMPLETED one — last_practice_session_id is simply the most recent attempt, which
        // is just as often an abandoned or still-analyzing one with no feedback behind it.
        const newest = sessions[0];
        const newestComplete = sessions.find((session: any) => session?.status === 'complete');
        return {
          screenContentId: action?.screen_content_id,
          // An empty title means the action is gone — the id is all there is left to name it by.
          title: (action?.title || '').trim() || `#${action?.screen_content_id ?? ''}`,
          description: (action?.description || action?.action_description || '').trim(),
          sessionCount: action?.session_count ?? sessions.length,
          completedCount: action?.completed_count ?? 0,
          scores: (action?.confidence_scores || []).filter((score: any) => typeof score === 'number'),
          recommendation: action?.recommendation ?? null,
          // The server's verdict, not a threshold applied here. Null (no completed attempt yet)
          // is not ready.
          isReady: action?.recommendation === 'ready',
          latestAt: action?.last_attempted_at || newest?.created_at || newest?.ended_at || '',
          // Empty when this page holds no completed attempt: the caller then opens the practice
          // bot's main screen rather than a report that would come back without feedback.
          latestSessionId: newestComplete?.practice_session_id || '',
          sessionIds: sessions.map((session: any) => session?.practice_session_id).filter(Boolean),
          sessions,
          hasMoreSessions: !!action?.has_more,
          ...this.cycleOf(action),
        } as PracticeHistoryGroup;
      })
      .filter((group: PracticeHistoryGroup) => group.screenContentId != null);
  }

  /**
   * Every attempt in a history response, flattened.
   *
   * /practice groups by micro-action now (regrouped 1 Sep 2026), so the attempts live under
   * actions[].sessions rather than at the top level. The older flat shapes are still read, because
   * a client can meet either while a deploy is rolling.
   */
  private sessionListFrom(response: any): any[] {
    const actions = response?.actions;
    if (Array.isArray(actions)) {
      return actions.reduce((all: any[], action: any) => {
        for (const session of (action?.sessions || [])) {
          // The action carries the title; the session does not. Attach it so a flattened attempt
          // can still say which micro-action it belongs to.
          all.push({ ...session, action_title: session?.action_title ?? action?.title });
        }
        return all;
      }, []);
    }
    const sessions = response?.sessions ?? response?.practice_sessions ?? response ?? [];
    return Array.isArray(sessions) ? sessions : [];
  }

  /** Just the attempts with a report to open. */
  completedAttemptsFrom(response: any): PracticeCompletedAttempt[] {
    return this.attemptsFrom(response).filter((attempt) => attempt.status === 'complete');
  }

  /** Attempts that ended without a report — the learner cancelled, or the analysis failed. */
  failedAttemptsFrom(response: any): PracticeCompletedAttempt[] {
    return this.attemptsFrom(response).filter((attempt) => attempt.status === 'failed');
  }

  deletePracticeSession(sessionId: string): Observable<any> {
    return this.http.delete(`${this.PRACTICE_URL}/${sessionId}`, { headers: this.authHeaders() });
  }

  /** Deletes every session recorded against one micro-action. */
  deletePracticeSessionsForAction(screenContentId: number | string): Observable<any> {
    const params = new HttpParams().set('screen_content_id', String(screenContentId));
    return this.http.delete(this.PRACTICE_URL, { headers: this.authHeaders(), params });
  }

  /** Deletes the user's entire practice history. */
  deleteAllPracticeSessions(): Observable<any> {
    const params = new HttpParams().set('scope', 'all');
    return this.http.delete(this.PRACTICE_URL, { headers: this.authHeaders(), params });
  }
}
